import {
  SupportingDocumentRule,
  type LeaveAttachmentContentType,
  type LeaveAttachmentInput,
  type LeaveAttachmentUploadTicket,
} from '@gokrew/models/leave-management';

const BY_EXTENSION: Record<string, LeaveAttachmentContentType> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  heic: 'image/heic',
};

/**
 * The content type the API takes for a picked file. Browsers often report
 * an iPhone photo (HEIC) with an empty `type`, so the extension decides then.
 */
export function leaveAttachmentContentType(
  file: File
): LeaveAttachmentContentType {
  const declared = file.type === 'image/jpg' ? 'image/jpeg' : file.type;
  if (Object.values(BY_EXTENSION).includes(declared as never)) {
    return declared as LeaveAttachmentContentType;
  }
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return BY_EXTENSION[extension] ?? 'application/pdf';
}

/**
 * Uploads picked supporting documents before a leave request is sent: one
 * presigned PUT per file (the app's `requestTicket` calls its own portal's
 * upload URL route), then the `attachments` the create or edit carries.
 */
export async function uploadLeaveAttachments(
  files: File[],
  requestTicket: (meta: {
    fileName: string;
    contentType: LeaveAttachmentContentType;
    sizeBytes: number;
  }) => Promise<LeaveAttachmentUploadTicket>
): Promise<LeaveAttachmentInput[]> {
  const uploaded: LeaveAttachmentInput[] = [];
  for (const file of files) {
    const meta = {
      fileName: file.name,
      contentType: leaveAttachmentContentType(file),
      sizeBytes: file.size,
    };
    const ticket = await requestTicket(meta);
    const response = await fetch(ticket.url, {
      method: 'PUT',
      headers: { 'Content-Type': meta.contentType },
      body: file,
    });
    if (!response.ok) {
      throw new Error(`upload failed (${response.status})`);
    }
    uploaded.push({ ...meta, key: ticket.key });
  }
  return uploaded;
}

/** Whether a rule needs at least one file before an employee submits. */
export const isAttachmentRequired = (
  rule: SupportingDocumentRule | null | undefined
) => rule === SupportingDocumentRule.Required;
